import {
  Box,
  Group,
  Text,
  Badge,
  ActionIcon,
  Menu,
  Avatar,
  Image,
  Stack,
} from '@mantine/core';
import { IconDots, IconTrash, IconPhoto, IconChevronRight } from '@tabler/icons-react';

interface AuditAssetMobileCardProps {
  item: {
    id: string;
    status: string;
    notes: string | null;
    completed_at: string | null;
    asset: {
      id: string;
      tag_id: string;
      description: string;
      photo_url: string | null;
      brand?: string;
      model?: string;
      location?: { name: string } | null;
    } | null;
  };
  onRemove: (id: string) => void;
  onClick?: () => void;
}

const statusColors: Record<string, string> = {
  pending: 'gray',
  found: 'green',
  missing: 'red',
  damaged: 'orange',
};

export function AuditAssetMobileCard({ item, onRemove, onClick }: AuditAssetMobileCardProps) {
  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid var(--mantine-color-gray-2)',
        backgroundColor: item.status !== 'pending' ? 'var(--mantine-color-gray-0)' : 'transparent',
      }}
    >
      {/* Main content - clickable */}
      <Box
        onClick={onClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flex: 1,
          padding: '12px 8px 12px 16px',
          cursor: onClick ? 'pointer' : 'default',
          minWidth: 0,
        }}
      >
        {/* Photo */}
        {item.asset?.photo_url ? (
          <Image
            src={item.asset.photo_url}
            alt={item.asset.description}
            w={48}
            h={48}
            radius="sm"
            fit="cover"
            style={{ flexShrink: 0 }}
          />
        ) : (
          <Avatar color="gray" size={48} radius="sm" style={{ flexShrink: 0 }}>
            <IconPhoto size={20} />
          </Avatar>
        )}

        {/* Content */}
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" wrap="nowrap" gap="xs">
            <Text fw={600} size="sm" truncate>
              {item.asset?.tag_id || 'Unknown'}
            </Text>
            <Badge color={statusColors[item.status]} variant="light" size="sm" style={{ flexShrink: 0 }}>
              {item.status}
            </Badge>
          </Group>

          <Text size="sm" truncate>
            {item.asset?.description || 'No description'}
          </Text>

          {item.asset?.location?.name && (
            <Text size="xs" c="dimmed" truncate>
              {item.asset.location.name}
            </Text>
          )}
        </Stack>

        {/* Chevron to indicate clickable */}
        {onClick && (
          <IconChevronRight size={20} color="var(--mantine-color-gray-5)" style={{ flexShrink: 0 }} />
        )}
      </Box>

      {/* Actions - separate from click area */}
      <Box
        style={{
          padding: '12px 16px 12px 8px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Menu position="bottom-end">
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={(e) => e.stopPropagation()}
            >
              <IconDots size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconTrash size={16} />}
              color="red"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(item.id);
              }}
            >
              Remove from Audit
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Box>
    </Box>
  );
}
